import type { AssetDetail, EventFromAsset } from '../../api/types';
import type { AssetModel, CandidateEvent } from './types';

function texts(
    list: readonly (string | null | undefined)[] | undefined
): string[] {
    return (list ?? []).filter(
        (item): item is string => typeof item === 'string' && item.trim() !== ''
    );
}

export function assetId(assetType: string, ticker: string): string {
    return `${assetType}:${ticker}`;
}

export function assetFromDetail(
    detail: AssetDetail | null,
    assetType: string,
    ticker: string
): AssetModel {
    const profile = detail?.data;
    const summary =
        detail?.description?.short_summary?.trim() ||
        profile?.description?.short_summary?.trim() ||
        null;
    return {
        id: assetId(assetType, ticker),
        ticker,
        assetType: detail?.asset_type || assetType,
        title: detail?.asset_name || ticker,
        categories: texts(detail?.classification?.sub_categories),
        summary,
        characteristics: texts(profile?.description?.key_characteristics),
        useCases: texts(profile?.description?.primary_use_cases),
    };
}

function toDate(value: string | null | undefined): Date | null {
    if (!value) return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
}

function toNumber(value: number | null | undefined): number | null {
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

export function candidateFromSearch(
    row: EventFromAsset,
    open: boolean
): CandidateEvent {
    const markets = (row.markets_summary ?? []).map(m => ({
        marketId: m.condition_id,
        label: m.yes_outcome || m.question || m.condition_id,
        probability: toNumber(m.yes_prob),
        volume: toNumber(m.volume) ?? 0,
    }));
    return {
        id: row.event_id,
        slug: row.slug || null,
        title: row.event_title,
        image: row.image ?? null,
        open,
        startDate: toDate(row.start_date),
        endDate: toDate(row.end_date),
        volume: toNumber(row.volume) ?? 0,
        score: toNumber(row.similarity) ?? 0,
        markets,
    };
}

export function candidatesFromSearch(
    active: readonly EventFromAsset[],
    closed: readonly EventFromAsset[]
): CandidateEvent[] {
    const seen = new Set<string>();
    const result: CandidateEvent[] = [];
    const add = (row: EventFromAsset, open: boolean) => {
        if (seen.has(row.event_id)) return;
        seen.add(row.event_id);
        result.push(candidateFromSearch(row, open));
    };
    active.forEach(row => add(row, true));
    closed.forEach(row => add(row, false));
    return result.sort((a, b) => b.score - a.score);
}

export function eventKey(event: CandidateEvent): string {
    return event.slug ?? event.id;
}
